import { ComponentRef, Directive, ElementRef, HostListener, Injector, OnDestroy, ViewContainerRef, inject, input } from '@angular/core';
import { TooltipComponent } from '../../app/shared/components/custom-tooltip/custom-tooltip.component';

export enum TooltipPosition {
  ABOVE = 'above',
  BELOW = 'below',
  LEFT = 'left',
  RIGHT = 'right',
}

export interface Directions {
  left: number;
  top: number;
  right?: number;
  bottom?: number;
}

@Directive({
  selector: '[appTooltip]',
  standalone: true,
})
export class TooltipDirective implements OnDestroy {
  readonly appTooltip = input.required<string | null>();
  readonly position = input<TooltipPosition>(TooltipPosition.BELOW);
  readonly targetCheck = input<boolean>(false);
  readonly tooltipWidth = input<number>(250);

  private componentRef: ComponentRef<TooltipComponent> | null = null;

  private elementRef = inject(ElementRef);
  private viewContainerRef = inject(ViewContainerRef);
  private injector = inject(Injector);

  @HostListener('mouseenter', ['$event'])
  onMouseEnter($event: MouseEvent): void {
    $event.stopPropagation();
    if (!this.targetCheck() || ($event.relatedTarget as HTMLElement)?.classList.value.split(' ').includes('tooltip-target')) {
      this.initializeTooltip();
    }
  }

  @HostListener('click')
  onClick(): void {
    this.destroy();
  }

  @HostListener('mouseleave')
  onMouseLeave(): void {
    this.destroy();
  }

  @HostListener('touchstart', ['$event'])
  onTouchStart($event: TouchEvent): void {
    $event.preventDefault();
    this.initializeTooltip();
  }

  @HostListener('touchend')
  onTouchEnd(): void {
    this.destroy();
  }

  private initializeTooltip(): void {
    if (this.componentRef) return;

    const tooltipId = `tooltip-${Math.random().toString(36).slice(2, 11)}`;
    this.elementRef.nativeElement.setAttribute('aria-describedby', tooltipId);

    this.componentRef = this.viewContainerRef.createComponent(TooltipComponent, {
      injector: this.injector,
    });

    const tooltipDOMElement = this.componentRef.location.nativeElement;
    tooltipDOMElement.id = tooltipId;

    this.setTooltipComponentProperties();

    document.body.appendChild(tooltipDOMElement);
    this.showTooltip();
  }

  private setTooltipComponentProperties(): void {
    if (!this.componentRef || !this.appTooltip()) return;
    this.componentRef.instance.tooltip = this.appTooltip() as string;
    this.componentRef.instance.position = this.position();
    this.componentRef.instance.tooltipWidth = this.tooltipWidth();

    const { left, right, top, bottom } = this.elementRef.nativeElement.getBoundingClientRect();

    const position = this.calculatePosition(left, right, top, bottom);
    this.componentRef.instance.left = position.left;
    this.componentRef.instance.top = position.top;
  }

  private calculatePosition(left: number, right: number, top: number, bottom: number): Directions {
    const { offsetWidth: tooltipWidth, offsetHeight: tooltipHeight } = this.componentRef!.location.nativeElement;

    const position: Directions = { left: 0, top: 0 };

    switch (this.position()) {
      case TooltipPosition.BELOW:
        position.left = Math.round((right - left) / 2 + left - tooltipWidth / 2);
        position.top = Math.round(bottom);
        break;
      case TooltipPosition.ABOVE:
        position.left = Math.round((right - left) / 2 + left - tooltipWidth / 2);
        position.top = Math.round(top - tooltipHeight);
        break;
      case TooltipPosition.RIGHT:
        position.left = Math.round(right);
        position.top = Math.round(top + (bottom - top) / 2 - tooltipHeight / 2);
        break;
      case TooltipPosition.LEFT:
        position.left = Math.round(left - tooltipWidth);
        position.top = Math.round(top + (bottom - top) / 2 - tooltipHeight / 2);
        break;
    }

    return position;
  }

  private showTooltip(): void {
    if (!this.componentRef) return;
    this.componentRef.instance.visible = true;
  }

  ngOnDestroy(): void {
    this.destroy();
  }

  destroy(): void {
    if (!this.componentRef) return;

    this.componentRef.instance.visible = false;
    this.viewContainerRef.detach();
    this.componentRef.destroy();
    this.componentRef = null;
  }
}
